import React, { useState } from 'react';
import { createRoot } from 'react-dom/client';
import { TeamMemberGridContext } from './TeamMemberGridContext';
import FilterContainer from '../Filter/FilterContainer';
import { removeDrawers } from './TeamMemberGridManager';

/**
 * Filters team members based on selected filters.
 *
 * @param {string} gridId - The grid ID.
 * @param {Array} imprintFilters - Selected imprint filters.
 * @param {Array} categoryFilters - Selected category filters.
 */
const filterTeamMembers = ( gridId, imprintFilters = [], categoryFilters = [] ) => {
  if ( !gridId ) return;

  const teamMembers = document.querySelectorAll( `.${gridId} .team-member` );
  let selectedTeamMemberCount = 0;

  teamMembers.forEach( ( member ) => {
    const memberImprints = member.dataset.imprintTags?.split( ' ' ).filter( Boolean ) || [];
    const memberCategories = member.dataset.categoryTags?.split( ' ' ).filter( Boolean ) || [];
    let show = false;

    if ( imprintFilters.length === 0 && categoryFilters.length === 0 ) {
      show = true;
    } else {
      if ( imprintFilters.some( ( filter ) => memberImprints.includes( filter ) ) ) {
        show = true;
      }
      if ( categoryFilters.some( ( filter ) => memberCategories.includes( filter ) ) ) {
        show = true;
      }
    }

    member.style.display = show ? 'flex' : 'none';
    if ( show ) selectedTeamMemberCount++;
  });

  // Display no results message if no team members match selected tags
  const gridContainer = document.querySelector( `.${gridId} .flex.flex-wrap` );
  const noResultsEl = gridContainer.querySelector( '.no-results' );
  if ( noResultsEl ) {
    noResultsEl.style.display = selectedTeamMemberCount === 0 ? 'block' : 'none';
  }
};

/**
 * Initializes the filters for the team member grid.
 */
export const initializeFilters = () => {
  const grid = document.querySelector( '.team-member-grid' );
  if ( !grid ) return;

  let gridId = grid.getAttribute( 'data-grid-id' );
  if ( !gridId ) {
    gridId = grid.classList.value.match( /team_grid_\w+/ )[0];
    grid.setAttribute( 'data-grid-id', gridId );
  }

  const data = window.DP[gridId];
  const filterContainer = document.getElementById( `team-filters-${gridId}` );

  if ( !filterContainer || !data?.filter_data?.enabled ) {
    return;
  }
  
  // Preprocess filter data into a format expected by filter UI.
  const filterDataConfig = {
    imprints: ( data.filter_data?.imprint_filter_options || [] ).map( ( item ) => ({
      label: item.name,
      value: item.slug
    }) ),
    categories: ( data.filter_data?.category_filter_options || [] ).map( ( item ) => ({
      label: item.name,
      value: item.slug
    }) )
  };

  const root = createRoot( filterContainer );
  root.render( <TeamMemberFilterWrapper gridId={gridId} filterData={filterDataConfig} /> );
};

/**
 * Wrapper component for managing team member filters.
 *
 * @param {Object} props - Component props.
 * @param {string} props.gridId - The grid ID.
 * @param {Object} props.filterData - The preprocessed filter data.
 */
const TeamMemberFilterWrapper = ({ gridId, filterData }) => {
  const [ filters, setFilters ] = useState({
    imprints: [],
    categories: []
  });

  const [ checked, setChecked ] = useState({
    imprints: {},
    categories: {}
  });

  const handleFilterChange = ( newFilters ) => {
    setFilters( newFilters );
    filterTeamMembers( gridId, newFilters.imprints, newFilters.categories );

    // Close any open drawers when filters are updated
    removeDrawers( gridId );
  };

  return (
    <TeamMemberGridContext.Provider value={{
      filters,
      setFilters: handleFilterChange,
      filterOptions: filterData,
      checked,
      setChecked
    }}
    >
      <FilterContainer
        context={{ gridId }}
        filterType="team-member-grid"
      />
    </TeamMemberGridContext.Provider>
  );
};